/**
 * MDXComponents - Custom components available in MDX blog posts
 */

import { Link } from "react-router-dom";
import type { ComponentType } from "react";
import { CharacterImage } from "../CharacterImage";
import type { DisciplineSlug } from "@jumpbean/shared";

/**
 * LeadMagnet - Inline CTA for lead magnets in blog posts
 */
function LeadMagnet({
  type,
  title,
  description,
  buttonText = "Get Free Access",
}: {
  type: "checklist" | "guide" | "challenge" | "newsletter";
  title: string;
  description: string;
  buttonText?: string;
}) {
  // Map lead magnet types to signup URLs with appropriate query params
  const ctaUrl = `/register?ref=blog&lead=${type}`;

  return (
    <div className="not-prose my-8 p-6 bg-gradient-to-br from-sunshine-50 to-magic-50 rounded-2xl border-2 border-sunshine-200">
      <h4 className="font-display font-bold text-body-xl text-neutral-900 mb-2">
        {title}
      </h4>
      <p className="font-body text-body-md text-neutral-600 mb-4">
        {description}
      </p>
      <Link
        to={ctaUrl}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full
                 font-display font-bold text-body-md text-neutral-900
                 bg-sunshine-400 hover:bg-sunshine-500
                 transition-colors shadow-sm hover:shadow-md"
      >
        {buttonText}
        <span>→</span>
      </Link>
    </div>
  );
}

/**
 * Callout - Styled callout boxes for tips, warnings, notes
 */
function Callout({
  type = "tip",
  children,
}: {
  type?: "tip" | "warning" | "note" | "info";
  children: React.ReactNode;
}) {
  const styles: Record<string, { bg: string; border: string; icon: string }> = {
    tip: {
      bg: "bg-meadow-50",
      border: "border-meadow-400",
      icon: "💡",
    },
    warning: {
      bg: "bg-encourage-light",
      border: "border-encourage",
      icon: "⚠️",
    },
    note: {
      bg: "bg-sky-50",
      border: "border-sky-400",
      icon: "📝",
    },
    info: {
      bg: "bg-magic-50",
      border: "border-magic-400",
      icon: "ℹ️",
    },
  };

  const style = styles[type] ?? styles.tip!;

  return (
    <div
      className={`not-prose my-6 p-4 border-l-4 rounded-r-lg ${style!.bg} ${style!.border}`}
    >
      <div className="flex gap-3">
        <span className="text-xl flex-shrink-0">{style!.icon}</span>
        <div className="font-body text-body-md text-neutral-700">{children}</div>
      </div>
    </div>
  );
}

/**
 * CharacterTip - Character callout with a JumpBean character
 */
function CharacterTip({
  character,
  children,
}: {
  character: DisciplineSlug;
  children: React.ReactNode;
}) {
  const characterNames: Record<DisciplineSlug, string> = {
    math: "Luna",
    spelling: "Petal",
    patterns: "Prism",
    typing: "Whiskers",
    programming: "Pixel",
  };

  return (
    <div className="not-prose my-8 flex flex-col sm:flex-row gap-4 p-6 bg-white rounded-2xl shadow-playful border-2 border-neutral-100">
      <div className="flex-shrink-0 flex justify-center">
        <CharacterImage discipline={character} expression="happy" size="lg" />
      </div>
      <div className="flex-1">
        <p className="font-display font-bold text-body-md text-neutral-500 mb-2">
          {characterNames[character]} says:
        </p>
        <div className="font-body text-body-md text-neutral-700">{children}</div>
      </div>
    </div>
  );
}

/**
 * FeatureHighlight - Highlight JumpBean features contextually
 */
function FeatureHighlight({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="not-prose my-8 p-6 bg-gradient-to-br from-magic-50 to-sky-50 rounded-2xl border border-magic-200">
      <h4 className="font-display font-bold text-body-lg text-neutral-900 mb-3">
        {title}
      </h4>
      <div className="font-body text-body-md text-neutral-700">{children}</div>
      <Link
        to="/register"
        className="inline-flex items-center gap-1 mt-4 font-body text-body-sm font-semibold text-magic-600 hover:text-magic-700"
      >
        Try JumpBean free <span>→</span>
      </Link>
    </div>
  );
}

/**
 * Export MDX components map for use in blog posts
 */
export const mdxComponents: Record<string, ComponentType<any>> = {
  LeadMagnet,
  Callout,
  CharacterTip,
  FeatureHighlight,
  // Override default anchor to use React Router Link for internal links
  a: ({
    href,
    children,
    ...props
  }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    const isInternal = href?.startsWith("/");
    if (isInternal && href) {
      return (
        <Link to={href} {...props}>
          {children}
        </Link>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  },
};
